import React, { useState, useMemo } from 'react';
import { Tabs, theme, Result } from 'antd';
import {
    FolderOutlined,
    MessageOutlined,
    AppstoreOutlined,
    TableOutlined,
    TrophyOutlined,
    BulbOutlined,
    ReadOutlined,
    ApartmentOutlined,
    TagsOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { categoryPermissions, nichePermissions, quotePermissions, rankMappingPermissions, designScalePermissions } from '../../utils/permissions';

import CategorySettings from './productCategories/components/CategorySettings';
import AutoMessageSettings from './autoMessages/components/AutoMessageSettings';
import MappingSettings from './mappingSettings/components/MappingSettings';
import RankMappingSettings from './rankMapping/components/RankMappingSettings';
import RuleSettings from './nicheQuote/components/RuleSettings';
import ProductLineSettings from './productLines/components/ProductLineSettings';
import DesignScale from '../DesignScale';
import './CategoryManagement.css';

const { TabPane } = Tabs;

const CategoryManagement = () => {
    const [activeKey, setActiveKey] = useState('product-categories');
    const { user } = useSelector(state => state.auth);
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Check permissions for each tab
    const canViewCategories = categoryPermissions.canRead(user);
    const canManageProductCategories = categoryPermissions.canManageProductCategories(user);
    const canManageAutoMessages = categoryPermissions.canManageAutoMessages(user);
    const canManageMappingSettings = categoryPermissions.canManageMappingSettings(user);
    const canViewRankMapping = rankMappingPermissions.canRead(user) || rankMappingPermissions.canManage(user);
    const canViewNiche = nichePermissions.canRead(user) || nichePermissions.canManage(user);
    const canViewQuote = quotePermissions.canRead(user) || quotePermissions.canManage(user);
    const canViewDesignScale = designScalePermissions.canRead(user) || designScalePermissions.canManage(user);

    // Define all possible tabs with their permission requirements
    const allTabs = [
        {
            key: 'product-categories',
            tab: <span><FolderOutlined /> Danh mục sản phẩm</span>,
            component: <CategorySettings user={user} />,
            permission: canManageProductCategories
        },
        {
            key: 'product-lines',
            tab: <span><ApartmentOutlined /> Dòng sản phẩm</span>,
            component: <ProductLineSettings />,
            permission: canManageProductCategories
        },
        {
            key: 'auto-messages',
            tab: <span><MessageOutlined /> Auto messages</span>,
            component: <AutoMessageSettings user={user} />,
            permission: canManageAutoMessages
        },
        {
            key: 'mapping-settings',
            tab: <span><TableOutlined /> Mapping sheet</span>,
            component: <MappingSettings user={user} />,
            permission: canManageMappingSettings
        },
        {
            key: 'rank-mapping',
            tab: <span><TrophyOutlined /> Rank Mapping</span>,
            component: <RankMappingSettings user={user} />,
            permission: canViewRankMapping
        },
        {
            key: 'niches',
            tab: <span><BulbOutlined /> Niche</span>,
            component: <RuleSettings type="niche" user={user} />,
            permission: canViewNiche
        },
        {
            key: 'quotes',
            tab: <span><ReadOutlined /> Quote</span>,
            component: <RuleSettings type="quote" user={user} />,
            permission: canViewQuote
        },
        {
            key: 'sku-template-group',
            tab: <span><TagsOutlined /> SKU Template Group</span>,
            component: <DesignScale embedded user={user} />,
            permission: canViewDesignScale
        }
    ];

    // Filter tabs based on permissions
    const availableTabs = useMemo(() => {
        return allTabs.filter(tab => tab.permission);
    }, [canManageProductCategories, canManageAutoMessages, canManageMappingSettings, canViewRankMapping, canViewNiche, canViewQuote, canViewDesignScale]);

    // Set default active key to first available tab
    const defaultActiveKey = useMemo(() => {
        if (availableTabs.length > 0) {
            // If current active key is not available, switch to first available
            if (!availableTabs.find(tab => tab.key === activeKey)) {
                return availableTabs[0].key;
            }
        }
        return activeKey;
    }, [availableTabs, activeKey]);

    // Update active key if it changed due to permission filtering
    React.useEffect(() => {
        if (defaultActiveKey !== activeKey) {
            setActiveKey(defaultActiveKey);
        }
    }, [defaultActiveKey, activeKey]);

    // If user can't view any categories, show access denied
    if (!canViewCategories && !canViewRankMapping && !canViewNiche && !canViewQuote && !canViewDesignScale) {
        return (
            <div style={{ margin: '24px 16px' }}>
                <Result
                    status="403"
                    title="403"
                    subTitle="Xin lỗi, bạn không có quyền truy cập vào quản lý danh mục."
                />
            </div>
        );
    }

    // If no tabs are available, show limited access message
    if (availableTabs.length === 0) {
        return (
            <div style={{ margin: '24px 16px' }}>
                <Result
                    status="warning"
                    title="Quyền truy cập hạn chế"
                    subTitle="Bạn có thể xem danh mục nhưng không có quyền quản lý các danh mục cụ thể."
                />
            </div>
        );
    }

    // Removed useEffect that called fetchProductCategories() to avoid duplicate API calls
    // CategorySettings component will handle its own data fetching

    const handleTabChange = (key) => {
        setActiveKey(key);
    };

    return (
        <div
            style={{
                margin: '24px 16px',
                borderRadius: borderRadiusLG,
                minHeight: 280,
            }}
        >
            <Tabs
                activeKey={activeKey}
                onChange={handleTabChange}
                type="card"
                tabBarGutter={5}
                className="category-management-tabs"
            >
                {availableTabs.map(tab => (
                    <TabPane
                        tab={tab.tab}
                        key={tab.key}
                    >
                        {tab.component}
                    </TabPane>
                ))}
            </Tabs>
        </div>
    );
};

export default CategoryManagement;
